import { EyeIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { getServerLog } from 'services/apis/admin/admin'
import { Input2, Modal, Pagination, PlainTable } from 'stories/components'
import { formatTime } from 'utils'

export interface IServerLogData {
  id: number
  identifier: string
  key: string
  status: string
  data: Record<string, any>
  error: Record<string, any>
  createdBy: string
  createdAt: string
}

const itemCountPerPage = 10

const statusClasses: Record<string, string> = {
  success: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
}

export const ServerLog = () => {
  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    orderBy: 'id',
    orderDir: '-1',
    pageNum: 0,
    limit: itemCountPerPage,
  })
  const [filterQuery, setFilterQuery] = useState(filters.query)

  const [logs, setLogs] = useState<IServerLogData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [total, setTotal] = useState<number>(0)
  const [isGetLogsOnce, setIsGetLogsOnce] = useState(false)
  const [selectedLog, setSelectedLog] = useState<IServerLogData | null>(null)

  const fetchLogs = async (filters: Record<string, any>) => {
    setIsLoading(true)
    try {
      const res = await getServerLog({
        query: filters.query.trim(),
        page: filters.pageNum,
        limit: filters.limit,
        orderBy: filters.orderBy,
        orderDir: filters.orderDir,
      })
      setLogs(res.data || [])
      setTotal(res.total || 0)
    } catch (error) {
      console.error('Failed to fetch server logs:', error)
      setLogs([])
      setTotal(0)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isGetLogsOnce) return
    const timeOutId = setTimeout(() => {
      if (!isLoading) {
        const newFilters = cloneDeep(filters)
        newFilters.query = filterQuery
        newFilters.pageNum = 0
        setFilters(newFilters)
        fetchLogs(newFilters)
      }
    }, 700)
    return () => clearTimeout(timeOutId)
  }, [filterQuery])

  useEffect(() => {
    fetchLogs(filters).then(() => {
      setIsGetLogsOnce(true)
    })
  }, [])

  const onChangeFilter = (key: string, value: any) => {
    const newFilters = cloneDeep(filters)
    newFilters[key] = value
    setFilters(newFilters)
    if (key === 'query') setFilterQuery(value)
    else fetchLogs(newFilters)
  }

  const onPageNavigate = (num: number) => {
    onChangeFilter('pageNum', num)
  }

  return (
    <div className="server-log-container">
      <LayoutLoading show={isLoading} />

      <h2 className="text-2xl font-semibold mb-4">Server Log</h2>

      <div className="mb-4">
        <Input2
          value={filterQuery}
          type="search"
          title="Search Server Logs"
          onChange={(value) => setFilterQuery(value)}
          className="w-120"
        />
      </div>

      <PlainTable
        header={['No', 'Identifier', 'Key', 'Status', 'Created By', 'Created At', 'Action']}
        data={logs.map((log, index) => [
          filters.pageNum * itemCountPerPage + index + 1,
          <div className="max-w-xs overflow-hidden text-ellipsis" title={log.identifier}>
            {log.identifier}
          </div>,
          <div className="max-w-xs overflow-hidden text-ellipsis" title={log.key}>
            {log.key}
          </div>,
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              statusClasses[log.status?.toLowerCase()] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {log.status}
          </span>,
          log.createdBy || '-',
          formatTime(log.createdAt),
          <div className="flex">
            <span className="text-shade-blue p-1 hover-shadow1 cursor-pointer" onClick={() => setSelectedLog(log)}>
              <EyeIcon className="w-4 h-4" />
            </span>
          </div>,
        ])}
      />

      <div className="mt-4 flex justify-center">
        <Pagination
          totalCount={total}
          itemCountPerPage={filters.limit}
          pageNum={filters.pageNum}
          onNavigate={onPageNavigate}
          onCountPerPageUpdate={(count) => onChangeFilter('limit', Number(count))}
        />
      </div>

      {selectedLog && (
        <Modal title="Server Log Detail" isOpen onClose={() => setSelectedLog(null)} titleCancel="" titleOkay="">
          <div className="w-[700px] text-sm">
            <table className="w-full">
              <tbody>
                {[
                  ['ID', selectedLog.id],
                  ['Identifier', selectedLog.identifier],
                  ['Key', selectedLog.key],
                  ['Status', selectedLog.status],
                  ['Created By', selectedLog.createdBy || '-'],
                  ['Created At', formatTime(selectedLog.createdAt, 'MM/dd/yyyy HH:mm:ss')],
                ].map(([label, value]) => (
                  <tr key={label as string} className="border-b">
                    <td className="py-2 pr-4 font-semibold text-gray-600 w-32 align-top">{label}</td>
                    <td className="py-2 text-gray-900">{value}</td>
                  </tr>
                ))}
                <tr className="border-b">
                  <td className="py-2 pr-4 font-semibold text-gray-600 w-32 align-top">Data</td>
                  <td className="py-2">
                    <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-60 whitespace-pre-wrap break-all">
                      {selectedLog.data ? JSON.stringify(selectedLog.data, null, 2) : '-'}
                    </pre>
                  </td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-semibold text-gray-600 w-32 align-top">Error</td>
                  <td className="py-2">
                    <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-60 whitespace-pre-wrap break-all">
                      {selectedLog.error ? JSON.stringify(selectedLog.error, null, 2) : '-'}
                    </pre>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  )
}
